import { Checkbox, FormControlLabel } from '@mui/material';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { ReactComponent as EmojiIcon } from '../../../../assets/icons/emoji.svg';
import { ReactComponent as SendIcon } from '../../../../assets/icons/send.svg';
import { ReactComponent as AttachmentIcon } from '../../../../assets/icons/attachment.svg';
import useAppDispatch from '../../../../hooks/useAppDispatch';
import useChatWebSocket from '../../../../hooks/useChatWebSocket';
import { UploadedFile } from '../../../../hooks/useFileUploadWithValidation';
import useMessageFileUpload from '../../../../hooks/useMessageFileUpload';
import { ChatSelectors } from '../../../../redux/chat/selectors';
import {
    searchMessages,
    replyMessage,
    resetReplyMessage,
    updateMessageStatus,
    websocketMessageDeleted,
    websocketMessageReceived,
    websocketFetchConversationFulfilled,
    websocketReactionUpdate,
    markAllMessagesRead,
    markMessageUnread
} from '../../../../redux/chat/slice';
import {
    fetchConversation,
    sendNewMessage,
    deleteExistingMessage
} from '../../../../redux/chat/service';
import { CONVERSATION_PAGE_SIZE } from '../../../../constants/chat';
import { userId, isAdmin } from '../../../../redux/auth/service';
import { useChatScroll } from '../../hooks/useChatScroll';
import HeaderControls from './components/HeaderControls';
import ConversationMessageList from './components/MessageList';
import ReplyComposer from './components/ReplyComposer';
import {
    ConversationMessage,
    MessageReaction,
    MessageSchema
} from 'src/types/chat';
import {
    AlertChip,
    AttachmentButton,
    AttachmentChip,
    AttachmentsBar,
    EmojiButton,
    EmojiPickerContainer,
    InputContainer,
    MessageInput,
    SendButton
} from './styled';
import { generateUniqueId } from 'src/utils/attachments';
import { addReaction, removeReaction } from 'src/api/chat';

type ConversationContentProps = {
    conversationId: number;
    entityId: string;
    showSearch?: boolean;
    allowReactions?: boolean;
};

const convertMessage = (
    message: MessageSchema,
    replySource?: MessageSchema
): ConversationMessage => {
    const timestamp = new Date(message.timestampEpochMs).toLocaleString(
        undefined,
        {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        }
    );

    return {
        ...message,
        replyMessage: replySource ?? message.replyMessage,
        displayTimestamp: timestamp,
        isOwn:
            message.senderUserId !== undefined &&
            message.senderUserId === userId()
    };
};

const buildConversationMessages = (
    list: MessageSchema[],
    lookup: Map<number, MessageSchema>
): ConversationMessage[] => {
    return list.map((message) => {
        const replySource =
            message.replyMessage ??
            (message.replyMessageId
                ? lookup.get(message.replyMessageId)
                : undefined);
        return convertMessage(message, replySource);
    });
};

const ConversationContent: React.FC<ConversationContentProps> = ({
    conversationId,
    entityId,
    showSearch = true,
    allowReactions = true
}) => {
    const dispatch = useAppDispatch();
    const {
        messages,
        isLoading,
        conversationError,
        viewPageNumber,
        searchTerm: storedSearchTerm,
        entityId: stateEntityId,
        replyMessage: draftReplyMessage,
        activeConversationId,
        lastReadMessageId,
        unreadMessageCount,
        targetMessageId
    } = ChatSelectors(conversationId);

    const { prepareForMessageRemoval, forceScrollToBottom } = useChatScroll({
        messages: messages ?? [],
        conversationId,
        lastReadMessageId
    });

    const effectiveEntityId = stateEntityId ?? entityId;
    const normalizedSearchTerm = storedSearchTerm ?? '';

    const [hoveredMessageId, setHoveredMessageId] = useState<number | null>(
        null
    );
    const [messageText, setMessageText] = useState('');
    const [searchTerm, setSearchTerm] = useState(normalizedSearchTerm);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [attachedFiles, setAttachedFiles] = useState<UploadedFile[]>([]);
    const [attachmentIds, setAttachmentIds] = useState<number[]>([]);
    const emojiButtonRef = useRef<HTMLButtonElement>(null);
    const messageInputRef = useRef<HTMLInputElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const lastFetchedRef = useRef<{
        conversationId: number;
        page: number;
    } | null>(null);

    const { handleFilesSelect, removeFile: removeAttachedFile } =
        useMessageFileUpload({
            conversationId,
            onFilesUpdate: setAttachedFiles,
            onFileComplete: (attachmentId) =>
                setAttachmentIds((prev) => [...prev, attachmentId]),
            onFileRemove: (attachmentId) =>
                setAttachmentIds((prev) =>
                    prev.filter((id) => id !== attachmentId)
                )
        });

    const messageLookup = useMemo(() => {
        const map = new Map<number, MessageSchema>();
        (messages ?? []).forEach((msg) => map.set(msg.id, msg));
        return map;
    }, [messages]);

    const messagesWithMetadata = useMemo(() => {
        return buildConversationMessages(messages ?? [], messageLookup);
    }, [messages, messageLookup]);

    const currentUserAlias = useMemo(() => {
        const currentUserId = userId();
        const ownMessage = (messages ?? []).find(
            (m) => m.senderUserId === currentUserId
        );
        return ownMessage?.senderAlias;
    }, [messages]);

    const reactionsMap = useMemo(() => {
        const map: Record<number, MessageReaction[]> = {};
        (messages ?? []).forEach((msg) => {
            if (msg.reactions && msg.reactions.length > 0) {
                map[msg.id] = msg.reactions
                    .filter((r) => r.users.length > 0)
                    .map((r) => ({
                        emoji: r.emoji,
                        users: r.users,
                        count: r.users.length
                    }));
            }
        });
        return map;
    }, [messages]);

    const composerReplyMessage = useMemo(() => {
        if (!draftReplyMessage) return undefined;
        const replySource = draftReplyMessage.replyMessageId
            ? messageLookup.get(draftReplyMessage.replyMessageId)
            : draftReplyMessage.replyMessage;
        return convertMessage(draftReplyMessage, replySource);
    }, [draftReplyMessage, messageLookup]);

    useEffect(() => {
        setSearchTerm(storedSearchTerm ?? '');
    }, [storedSearchTerm]);

    useEffect(() => {
        if (!showEmojiPicker) {
            return;
        }

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            // Check if click is inside emoji picker container or button
            const pickerContainer = target.closest(
                '[data-emoji-picker-container]'
            );
            const isInsidePicker =
                pickerContainer !== null ||
                target.closest('.emoji-picker-react') !== null;
            const isInsideButton =
                emojiButtonRef.current?.contains(target) ?? false;

            // Don't close if clicking inside the picker - let the emoji click handler manage that
            if (!isInsidePicker && !isInsideButton) {
                setShowEmojiPicker(false);
            }
        };

        // Use bubble phase (not capture) so emoji picker click handlers fire first
        document.addEventListener('click', handleClickOutside);

        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [showEmojiPicker]);

    const [wsConnected, setWsConnected] = useState<boolean | null>(null);

    const { fetchMessages } = useChatWebSocket({
        conversationId: conversationId > 0 ? conversationId : null,
        entityId: effectiveEntityId,
        onNewMessage: useCallback(
            (message: MessageSchema) => {
                dispatch(websocketMessageReceived(message));
            },
            [dispatch]
        ),
        onMessageDeleted: useCallback(
            (convId: number, msgId: number) => {
                dispatch(
                    websocketMessageDeleted({
                        conversationId: convId,
                        messageId: msgId
                    })
                );
            },
            [dispatch]
        ),
        onConnectionStatusChange: useCallback((connected: boolean) => {
            setWsConnected(connected);
        }, []),
        onReactionUpdate: useCallback(
            (
                convId: number,
                msgId: number,
                reactions: { emoji: string; users: string[] }[]
            ) => {
                dispatch(
                    websocketReactionUpdate({
                        conversationId: convId,
                        messageId: msgId,
                        reactions
                    })
                );
            },
            [dispatch]
        )
    });

    useEffect(() => {
        if (
            conversationId !== lastFetchedRef.current?.conversationId ||
            activeConversationId !== conversationId
        ) {
            lastFetchedRef.current = null;
        }
    }, [conversationId, activeConversationId]);

    useEffect(() => {
        const shouldFetch =
            conversationId > 0 &&
            conversationId === activeConversationId &&
            viewPageNumber > 0 &&
            !isLoading &&
            (!lastFetchedRef.current ||
                lastFetchedRef.current.conversationId !== conversationId ||
                lastFetchedRef.current.page !== viewPageNumber);

        if (shouldFetch) {
            lastFetchedRef.current = { conversationId, page: viewPageNumber };
            const arg = {
                conversationId,
                entityId: effectiveEntityId,
                query: normalizedSearchTerm
            };
            fetchMessages({
                conversationId,
                entityId: effectiveEntityId,
                page: viewPageNumber,
                resultsPerPage: CONVERSATION_PAGE_SIZE,
                query: normalizedSearchTerm
            })
                .then((data) => {
                    dispatch(
                        websocketFetchConversationFulfilled({ data, arg })
                    );
                })
                .catch(() => {
                    // WS not connected yet — fall back to REST
                    if (
                        lastFetchedRef.current?.conversationId ===
                        conversationId
                    ) {
                        lastFetchedRef.current = null;
                    }
                    dispatch(
                        fetchConversation({
                            conversationId,
                            page: viewPageNumber,
                            resultsPerPage: CONVERSATION_PAGE_SIZE,
                            entityId: effectiveEntityId,
                            query: normalizedSearchTerm
                        })
                    );
                });
        }
    }, [
        dispatch,
        conversationId,
        activeConversationId,
        effectiveEntityId,
        normalizedSearchTerm,
        viewPageNumber,
        isLoading,
        fetchMessages
    ]);

    const isUploading = attachedFiles.some((f) => f.status === 'uploading');

    const uploadErrors = useMemo(
        () =>
            attachedFiles.flatMap((f) =>
                f.status === 'error' && f.errorMessage ? [f.errorMessage] : []
            ),
        [attachedFiles]
    );

    const handleSendMessage = () => {
        const hasText = messageText.trim().length > 0;
        const hasAttachments = attachmentIds.length > 0;
        if ((!hasText && !hasAttachments) || !conversationId || isUploading) {
            return;
        }

        const timestampEpochMs = Date.now();

        dispatch(
            sendNewMessage({
                conversationId,
                entityId: effectiveEntityId,
                tagId: '',
                text: messageText.trim(),
                timestampEpochMs,
                replyMessageId: draftReplyMessage?.id,
                attachmentIds:
                    attachmentIds.length > 0 ? attachmentIds : undefined
            })
        );

        setMessageText('');
        setAttachedFiles([]);
        setAttachmentIds([]);
        forceScrollToBottom();
        if (draftReplyMessage) {
            dispatch(resetReplyMessage(conversationId));
        }
    };

    const handleFileChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.files && e.target.files.length > 0) {
                const filesWithIds = Array.from(e.target.files).map((file) => ({
                    file,
                    id: generateUniqueId()
                }));
                handleFilesSelect(filesWithIds);
            }
            if (e.target) {
                e.target.value = '';
            }
        },
        [handleFilesSelect]
    );

    const handleAttachmentClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSendMessage();
        }
    };

    const handleHoverMessage = (messageId: number | null) => {
        setHoveredMessageId(messageId);
    };

    const handleSearch = (term: string) => {
        if (!conversationId) return;
        dispatch(
            searchMessages({
                conversationId,
                term: term.trim()
            })
        );
    };

    const handleReply = useCallback(
        (messageData: ConversationMessage) => {
            dispatch(replyMessage(messageData));
            forceScrollToBottom();
        },
        [dispatch, forceScrollToBottom]
    );

    const handleDelete = useCallback(
        (messageData: ConversationMessage) => {
            prepareForMessageRemoval();
            dispatch(
                updateMessageStatus({
                    conversationId: messageData.conversationId,
                    messageId: messageData.id,
                    isDeleted: true
                })
            );
            dispatch(
                deleteExistingMessage({
                    conversationId: messageData.conversationId,
                    messageId: messageData.id
                })
            );
        },
        [dispatch, prepareForMessageRemoval]
    );

    const handleMarkUnread = useCallback(
        (messageData: ConversationMessage) => {
            dispatch(
                markMessageUnread({
                    conversationId: messageData.conversationId,
                    messageId: messageData.id
                })
            );
        },
        [dispatch]
    );

    const handleMarkAllRead = useCallback(() => {
        dispatch(markAllMessagesRead({ conversationId }));
    }, [dispatch, conversationId]);

    const handleEmojiClick = useCallback((emojiData: EmojiClickData) => {
        const emoji = emojiData.emoji;
        if (!emoji) {
            return;
        }
        setMessageText((prev) => prev + emoji);
        setShowEmojiPicker(false);
        // Focus the input after a brief delay to ensure state is updated
        setTimeout(() => {
            if (messageInputRef.current) {
                messageInputRef.current.focus();
                // Move cursor to end of input
                const length = messageInputRef.current.value.length;
                messageInputRef.current.setSelectionRange(length, length);
            }
        }, 0);
    }, []);

    const handleToggleEmojiPicker = () => {
        setShowEmojiPicker((prev) => !prev);
    };

    const handleReaction = useCallback(
        async (messageId: number, emoji: string) => {
            if (!allowReactions) {
                return;
            }

            const message = (messages ?? []).find((m) => m.id === messageId);
            const currentReactions = message?.reactions ?? [];
            const existingGroup = currentReactions.find(
                (r) => r.emoji === emoji
            );
            const hasReacted = currentUserAlias
                ? (existingGroup?.users.includes(currentUserAlias) ?? false)
                : false;

            // Optimistic update so the UI responds immediately
            const optimisticReactions = hasReacted
                ? currentReactions
                      .map((r) =>
                          r.emoji === emoji
                              ? {
                                    ...r,
                                    users: r.users.filter(
                                        (u) => u !== currentUserAlias
                                    )
                                }
                              : r
                      )
                      .filter((r) => r.users.length > 0)
                : existingGroup
                  ? currentReactions.map((r) =>
                        r.emoji === emoji && currentUserAlias
                            ? { ...r, users: [...r.users, currentUserAlias] }
                            : r
                    )
                  : currentUserAlias
                    ? [
                          ...currentReactions,
                          { emoji, users: [currentUserAlias] }
                      ]
                    : currentReactions;

            dispatch(
                websocketReactionUpdate({
                    conversationId,
                    messageId,
                    reactions: optimisticReactions
                })
            );

            try {
                if (hasReacted) {
                    await removeReaction(conversationId, messageId, emoji);
                } else {
                    await addReaction(conversationId, messageId, emoji);
                }
            } catch (error) {
                console.error('Failed to update reaction:', error);
                // Revert optimistic update on error
                dispatch(
                    websocketReactionUpdate({
                        conversationId,
                        messageId,
                        reactions: currentReactions
                    })
                );
            }
        },
        [allowReactions, messages, currentUserAlias, conversationId, dispatch]
    );

    return (
        <>
            {showSearch && (
                <HeaderControls
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    onSearchSubmit={handleSearch}
                    wsConnected={wsConnected}
                />
            )}
            {isAdmin() && (
                <FormControlLabel
                    control={
                        <Checkbox
                            size="small"
                            checked={unreadMessageCount === 0}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    handleMarkAllRead();
                                }
                            }}
                        />
                    }
                    label="Mark all as read"
                    sx={{ px: 1, py: 0.25, m: 0, alignSelf: 'flex-start' }}
                />
            )}

            <ConversationMessageList
                messages={messagesWithMetadata}
                conversationId={conversationId}
                isLoading={
                    Boolean(isLoading) ||
                    ((messages ?? []).length === 0 &&
                        (viewPageNumber === 0 || targetMessageId != null))
                }
                conversationError={conversationError}
                hoveredMessageId={hoveredMessageId}
                onHover={handleHoverMessage}
                onReply={handleReply}
                onDelete={handleDelete}
                onReact={handleReaction}
                onMarkUnread={handleMarkUnread}
                reactionsMap={reactionsMap}
                allowReactions={allowReactions}
                lastReadMessageId={lastReadMessageId}
                unreadMessageCount={unreadMessageCount}
                targetMessageId={targetMessageId}
                currentUserAlias={currentUserAlias}
            />

            {composerReplyMessage && (
                <ReplyComposer
                    message={composerReplyMessage}
                    onCancel={() => dispatch(resetReplyMessage(conversationId))}
                />
            )}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={handleFileChange}
                style={{ display: 'none' }}
            />

            {attachedFiles.length > 0 && (
                <AttachmentsBar>
                    {attachedFiles.map((f) => {
                        const maxLen = 30;
                        const truncated =
                            f.file.name.length > maxLen
                                ? `${f.file.name.slice(0, maxLen)}…`
                                : f.file.name;
                        const progress = f.uploadProgress ?? 0;
                        const label =
                            f.status === 'uploading'
                                ? `${truncated} (${progress > 0 ? `${progress}%` : 'Uploading...'})`
                                : f.status === 'error'
                                  ? `${truncated} (failed)`
                                  : truncated;
                        const sx =
                            f.status === 'error'
                                ? {
                                      color: '#ff5252',
                                      backgroundColor:
                                          'rgba(255, 82, 82, 0.12)',
                                      '& .MuiChip-deleteIcon': {
                                          color: '#ff5252'
                                      }
                                  }
                                : f.status === 'completed'
                                  ? {
                                        color: '#66bb6a',
                                        backgroundColor:
                                            'rgba(102, 187, 106, 0.12)',
                                        '& .MuiChip-deleteIcon': {
                                            color: '#66bb6a'
                                        }
                                    }
                                  : {};
                        return (
                            <AttachmentChip
                                key={f.id}
                                label={label}
                                size="small"
                                onDelete={() => removeAttachedFile(f.id)}
                                sx={sx}
                            />
                        );
                    })}
                </AttachmentsBar>
            )}
            {uploadErrors.length > 0 && (
                <AttachmentsBar>
                    {uploadErrors.map((msg, i) => (
                        <AlertChip key={i} label={msg} size="small" />
                    ))}
                </AttachmentsBar>
            )}

            <InputContainer>
                <EmojiPickerContainer>
                    <EmojiButton
                        ref={emojiButtonRef}
                        size="small"
                        onClick={handleToggleEmojiPicker}
                    >
                        <EmojiIcon />
                    </EmojiButton>
                    {showEmojiPicker && (
                        <div
                            data-emoji-picker-container
                            style={{
                                position: 'absolute',
                                bottom: '100%',
                                left: 0,
                                marginBottom: '8px',
                                zIndex: 1000
                            }}
                        >
                            <EmojiPicker
                                onEmojiClick={handleEmojiClick}
                                width={350}
                                height={400}
                            />
                        </div>
                    )}
                </EmojiPickerContainer>
                <AttachmentButton
                    size="small"
                    title="Attach image or video"
                    onClick={handleAttachmentClick}
                >
                    <AttachmentIcon />
                </AttachmentButton>
                <MessageInput
                    placeholder="Abracadabra! :)"
                    variant="outlined"
                    size="small"
                    fullWidth
                    multiline
                    maxRows={4}
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onKeyDown={handleKeyPress}
                    inputRef={messageInputRef}
                />
                <SendButton
                    onClick={handleSendMessage}
                    disabled={
                        !conversationId ||
                        (!messageText.trim() && attachmentIds.length === 0) ||
                        isUploading
                    }
                >
                    <SendIcon width={18} height={18} />
                </SendButton>
            </InputContainer>
        </>
    );
};

export default ConversationContent;
